import React from 'react';
import {
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Divider,
} from '@mui/material';

// Sentinel values for the Select-all / Clear-all row. Prefixed so they can never collide with a
// real status value. (A MUI Select uses MenuItems for its header — Autocomplete's PaperComponent
// does NOT work on Select, which is why this uses in-menu sentinel items.)
const TOGGLE_ALL = '__status_toggle_all__';

/**
 * Reusable multi-select status filter — one consistent status filter for every screen
 * (Rishabh 2026-07-30: "all status filters system-wide consistent and user friendly").
 *
 * Fixes the whole class of "shows N selected when I picked fewer, and I can't untick it" bugs:
 * the count and the checkboxes are ALWAYS computed against the options the user can actually see,
 * so a stray value in the stored filter (a status with no checkbox) can never inflate the count.
 * Also gives every filter a "Select all / Clear all" row.
 *
 * Props:
 *   label     - field label
 *   options   - [{ value, label }] the selectable statuses (what the user sees)
 *   value     - array of currently-selected values (stray values not in `options` are ignored)
 *   onChange  - (nextArray) => void  — receives only values that exist in `options`
 *   size, minWidth - passthrough styling
 *
 * Any screen-specific mapping (e.g. "shipped implies delivered") stays in the caller's onChange
 * wrapper — this component only ever deals in the visible options, which is what keeps it honest.
 */
export default function StatusMultiSelect({ label, options, value, onChange, size = 'small', minWidth }) {
  const optionValues = options.map((o) => o.value);
  // Honest selection: only values that correspond to a real, visible checkbox.
  const selected = (value || []).filter((v) => optionValues.includes(v));
  const allSelected = options.length > 0 && selected.length === options.length;

  const renderValue = (sel) => {
    const s = (sel || []).filter((v) => optionValues.includes(v));
    if (s.length === 0 || s.length === options.length) return 'All';
    if (s.length === 1) {
      const o = options.find((x) => x.value === s[0]);
      return o ? o.label : s[0];
    }
    return `${s.length} selected`;
  };

  return (
    <FormControl fullWidth size={size} sx={minWidth ? { minWidth } : undefined}>
      {/* shrink: with displayEmpty + a renderValue of 'All', MUI keeps the label in the field and
          it overlaps the value, so float it permanently (same fix as the Retail country filter). */}
      <InputLabel shrink>{label}</InputLabel>
      <Select
        multiple
        displayEmpty
        notched
        label={label}
        value={selected}
        renderValue={renderValue}
        onChange={(e) => {
          const raw = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          // The Select-all / Clear-all row is a sentinel MenuItem; clicking it lands in `raw`.
          if (raw.includes(TOGGLE_ALL)) {
            onChange(allSelected ? [] : optionValues);
            return;
          }
          onChange(raw.filter((x) => optionValues.includes(x)));
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 380 } } }}
      >
        <MenuItem value={TOGGLE_ALL} dense sx={{ color: 'primary.main', fontSize: 13, py: 0.5 }}>
          {allSelected ? 'Clear all' : 'Select all'}
        </MenuItem>
        <Divider />
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ py: 0.5 }}>
            <Checkbox checked={selected.includes(o.value)} size="small" sx={{ p: 0.5, mr: 1 }} />
            <ListItemText
              primary={o.label}
              primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontSize: 13 } }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
